import { ChangeEvent, useState, useContext } from 'react'
import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  Stack,
  HStack,
  Spinner,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { AdSense, MultiSelect, Select, TextArea } from 'components'
import styled from '@emotion/styled'

import { fetchSongs, Prompt, Song } from 'services/openai'
import { createSearch } from 'services/firebase'
import { userContext } from 'context/userContext'

import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { themes } from 'data/themes'
import { moods } from 'data/moods'
import { genres } from 'data/genres'
import { templates } from 'data/templates'
import { Template } from 'types/data'
import { Search } from 'types/search'
import { SongTable } from 'components/general/SongTable'

const schema = yup
  .object({
    theme: yup.string().required('Please select a theme'),
    moods: yup
      .array()
      .of(yup.string())
      .min(1, 'Please select at least one mood')
      .required('Please select at least one mood'),
    genre: yup.string(),
    songCount: yup.number(),
    description: yup.string().required('Please enter a description'),
    template: yup.string(),
  })
  .required()

export const Home = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [songs, setSongs] = useState<Song[]>()
  const toast = useToast()
  const { control, handleSubmit, setValue, getValues, watch } = useForm<{
    theme: string
    moods: (string | undefined)[]
    genre: string | undefined
    songCount: number | undefined
    description: string
    template: string | undefined
  }>({
    resolver: yupResolver(schema),
    defaultValues: {
      theme: '',
      moods: [],
      genre: '',
      songCount: 10,
      description: '',
      template: '',
    },
  })
  const theme = watch('theme')
  const songCount = watch('songCount')
  const description = watch('description')
  const isFormValid =
    theme &&
    selectedOptions.length > 0 &&
    songCount &&
    Number(songCount) >= 5 &&
    Number(songCount) <= 25 &&
    description
  const { user } = useContext(userContext)
  const userIsLoggedIn = Boolean(user.data)

  const formatData = (data: string[]) => {
    return data.map((d) => {
      return { label: d, value: d.toLocaleLowerCase() }
    })
  }

  const formatTemplateOptions = (data: Template[]) => {
    return data.map((template: Template) => {
      return { label: template.name, value: template.name.toLocaleLowerCase() }
    })
  }

  const handleTemplateChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedTemplate = templates.find((t) => t.name.toLocaleLowerCase() === e.target.value)

    if (selectedTemplate) {
      setValue('theme', selectedTemplate.theme.toLocaleLowerCase())
      setValue('description', selectedTemplate.description)
      setSelectedOptions(selectedTemplate.moods)
    }
  }

  const handleSelectedOptions = (arr: string[]) => {
    setSelectedOptions(arr)
  }

  const resetForm = () => {
    setSongs(undefined)
  }

  const onSearch = async () => {
    setLoading(true)
    try {
      const { theme, genre, songCount, description } = getValues()
      const moods = selectedOptions.map((mood) => mood.toLocaleLowerCase())
      const date = new Date()
      const formattedDate = date.toISOString()
      const userId = user?.data?.uid

      const prompt: Prompt = {
        theme,
        moods,
        genre,
        songCount,
        description,
      }
      const songs = await fetchSongs(prompt)
      setSongs(songs)

      if (userIsLoggedIn && userId) {
        const search: Search = {
          theme,
          moods,
          genre,
          description,
          songs: songs.map((song: Song) => {
            return { artist: song.artist, title: song.title }
          }),
          createdAt: formattedDate,
          userId,
        }
        await createSearch(search)
      }

      toast({
        title: 'Success',
        status: 'success',
        isClosable: true,
      })
    } catch (e) {
      console.error(e)
      toast({
        title: 'Something went wrong. Please try again',
        status: 'error',
        isClosable: true,
      })
      setSongs(undefined)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box w='100%' p={4} pb={8}>
      <Container maxW='3xl'>
        <AdWrapper>
          <AdSense dataAdSlot='7685568922' />
        </AdWrapper>
        {loading ? (
          <LoadingWrapper>
            <Spinner size='xl' />
          </LoadingWrapper>
        ) : songs && songs?.length > 0 ? (
          <>
            <SongTable songs={songs} />
            <ButtonWrapper>
              <Button colorScheme='gray' width='100%' onClick={resetForm}>
                Return
              </Button>
            </ButtonWrapper>
          </>
        ) : (
          <form onSubmit={handleSubmit(onSearch)}>
            <Stack spacing={4}>
              <HStack alignItems='end'>
                <Controller
                  name='theme'
                  control={control}
                  render={({ field }) => (
                    <Select
                      label='Theme *'
                      placeholder='Select Theme'
                      options={formatData(themes)}
                      isDisabled={loading}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name='moods'
                  control={control}
                  render={() => (
                    <FormControl>
                      <MultiSelect
                        label='Mood *'
                        placeholder='Select mood(s)'
                        options={moods.map((mood) => mood)}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={handleSelectedOptions}
                        isDisabled={loading}
                      />
                    </FormControl>
                  )}
                />
              </HStack>
              <Controller
                name='genre'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Genre'
                    placeholder='Select Genre'
                    options={formatData(genres)}
                    isDisabled={loading}
                    {...field}
                  />
                )}
              />

              <VStack align='left'>
                <Text>Song Count (min 5, max 25) *</Text>
                <Controller
                  name='songCount'
                  control={control}
                  render={({ field }) => (
                    <NumberInput
                      defaultValue={10}
                      min={5}
                      max={25}
                      allowMouseWheel
                      isRequired
                      {...field}
                    >
                      <NumberInputField disabled />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  )}
                />
              </VStack>

              <Controller
                name='description'
                control={control}
                render={({ field }) => (
                  <TextArea
                    label='Describe your moment or song *'
                    placeholder='Ex. A nostalgic tune that incorporates elements of the past and evokes a sense of longing and reflection.'
                    helperText='* Required'
                    isDisabled={loading}
                    {...field}
                  />
                )}
              />

              <Divider mt={4} mb={2} />

              <Controller
                name='description'
                control={control}
                render={() => (
                  <Select
                    label='Templates'
                    placeholder='Select Template'
                    helperText='** This will override current selections and description. These templates can be used to help you quickly select your options or be a good starting point'
                    options={formatTemplateOptions(templates)}
                    onChange={handleTemplateChange}
                    isDisabled={loading}
                  />
                )}
              />

              <ButtonWrapper>
                <Button
                  type='submit'
                  colorScheme='gray'
                  isLoading={loading}
                  loadingText='Loading'
                  width='100%'
                  onClick={onSearch}
                  isDisabled={!isFormValid}
                >
                  Search
                </Button>
              </ButtonWrapper>
            </Stack>
          </form>
        )}
      </Container>
    </Box>
  )
}

const AdWrapper = styled.div`
  margin: 2rem 0;
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 25rem;
`

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: 2rem;
`
